import { useEffect, useState } from "react";

import { sampleRecordsInObj } from "auth/actions/objActions";

/**
 * Quantidade de registros de um formulário, para a coluna "Registros".
 *
 * Não há endpoint agregado por unidade — cada linha busca o próprio total. Por
 * isso a requisição só sai quando a linha entra na viewport, passa por um cache
 * com TTL curto e é cancelada se a linha sair da tela antes da resposta.
 */

const CACHE_TTL_MS = 2 * 60 * 1000;
const cache = new Map();

// Limpa os totais guardados (usado quando a listagem é recarregada)
export const clearFormSummaryCache = () => cache.clear();

const isAbort = (error) =>
  error?.name === "CanceledError" || error?.code === "ERR_CANCELED" || error?.name === "AbortError";

/**
 * Marca a linha como visível uma única vez — o total não precisa recarregar
 * quando a linha sai e volta para a tela.
 */
export function useInViewport(elementRef) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || isVisible) return undefined;

    // Sem IntersectionObserver (navegador antigo/JSDOM) carrega direto,
    // preferindo o dado presente ao lazy loading
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "120px" }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [elementRef, isVisible]);

  return isVisible;
}

export function useFormSummary(formName, enabled) {
  const [state, setState] = useState({ total: null, error: false });

  useEffect(() => {
    if (!enabled || !formName) return undefined;

    const entry = cache.get(formName);
    if (entry && Date.now() - entry.at <= CACHE_TTL_MS) {
      setState({ total: entry.value, error: false });
      return undefined;
    }

    const controller = new AbortController();
    setState({ total: null, error: false });

    sampleRecordsInObj({ formName, pageSize: 1, signal: controller.signal })
      .then((response) => {
        if (!response?.success) throw new Error(response?.message || "Falha ao obter registros");
        const total = response.totalInIndex || response.total || 0;
        cache.set(formName, { at: Date.now(), value: total });
        setState({ total, error: false });
      })
      .catch((error) => {
        if (isAbort(error)) return;
        setState({ total: null, error: true });
      });

    return () => controller.abort();
  }, [formName, enabled]);

  return state;
}
